/**
 * The plain `texteditor` (a textarea) with a bound `selection`
 * (docs/ops-protocol.md, "Rich text editors"): it reports the viewer's
 * selection (`selectionStart`/`selectionEnd`, UTF-16 offsets as a string's
 * indices) and moves to one the app sets, under the rich editor's rules
 * (src/editorSelection.ts): at once while focused, else when the app focuses
 * it; the selection the app sends back is not applied again, and one the
 * viewer did not move is not reported again.
 */

import type { TextEditorProps, TextSelectionRange } from './protocol';
import { editorBlurred, isSelectionBound, markSelectionBound, noteViewerSelection, selectionToApply, shiftOffset, takePendingSelection, viewerFocused } from './editorSelection';

/** A `text` that came from outside, with the editor's value and caret before it was applied (src/editorSelection.ts, `textArrived`). */
export interface OutsideChange {
  before: string;
  caret: TextSelectionRange | null;
}

export function isPlainEditor(el: unknown): el is HTMLTextAreaElement {
  return el instanceof HTMLTextAreaElement && el.classList.contains('sb-texteditor');
}

/**
 * After the text: a selection the app set (not the viewer's coming back),
 * moved to now, or when the app focuses the editor. After a text from
 * outside (`outside`): the app's selection even when its numbers did not
 * change, else the viewer's caret moved past the change (setting a
 * textarea's value puts it at the end otherwise).
 */
export function applyPlainEditorSelection(area: HTMLTextAreaElement, props: TextEditorProps, outside: OutsideChange | null = null): void {
  markSelectionBound(area, props.selection !== undefined);
  if (outside) {
    if (props.selection !== undefined) {
      const range = selectionToApply(area, props.selection, true);
      if (range) area.setSelectionRange(range.start, range.end);
    } else if (outside.caret) {
      area.setSelectionRange(shiftOffset(outside.before, area.value, outside.caret.start), shiftOffset(outside.before, area.value, outside.caret.end));
    }
    return;
  }
  const range = selectionToApply(area, props.selection);
  if (range) area.setSelectionRange(range.start, range.end);
}

/** Focuses an editor for the app (a bound `focused`): its caret where the app last set the selection, else where the browser leaves it. */
export function focusPlainEditor(area: HTMLTextAreaElement): void {
  const range = takePendingSelection(area);
  area.focus({ preventScroll: true });
  if (range) area.setSelectionRange(range.start, range.end);
}

type Report = (event: { type: 'selection'; id: number; value: TextSelectionRange }) => void;

/**
 * The listeners every plain editor under `root` shares: the selection
 * reported for bound editors. Returns what removes them.
 */
export function listenToPlainEditors(root: HTMLElement, idOf: (el: HTMLElement) => number | null, report: Report): () => void {
  const doc = root.ownerDocument;
  const editorOf = (target: EventTarget | null): HTMLTextAreaElement | null => (isPlainEditor(target) && root.contains(target) ? target : null);
  const reportSelection = (area: HTMLTextAreaElement): void => {
    if (!isSelectionBound(area)) return;
    const id = idOf(area);
    if (id === null) return;
    const range = { start: area.selectionStart, end: area.selectionEnd };
    if (noteViewerSelection(area, range)) report({ type: 'selection', id, value: range });
  };
  // Browsers differ in what a caret move in a textarea raises (the document's
  // `selectionchange`, the control's `select`, or nothing until the key or
  // button is up, as after an input): each is a look at the selection, which
  // is reported once however many of them fire.
  const onSelectionChange = (): void => {
    const area = editorOf(doc.activeElement);
    if (area) reportSelection(area);
  };
  const onEditorEvent = (event: Event): void => {
    const area = editorOf(event.target);
    if (area) reportSelection(area);
  };
  const onFocusIn = (event: FocusEvent): void => {
    const area = editorOf(event.target);
    if (area) viewerFocused(area);
  };
  const onFocusOut = (event: FocusEvent): void => {
    const area = editorOf(event.target);
    if (area) editorBlurred(area);
  };
  const looks = ['select', 'keyup', 'mouseup', 'input'] as const;
  for (const type of looks) root.addEventListener(type, onEditorEvent);
  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('focusout', onFocusOut);
  doc.addEventListener('selectionchange', onSelectionChange);
  return () => {
    for (const type of looks) root.removeEventListener(type, onEditorEvent);
    root.removeEventListener('focusin', onFocusIn);
    root.removeEventListener('focusout', onFocusOut);
    doc.removeEventListener('selectionchange', onSelectionChange);
  };
}
